<template>
  <div class="row overflow-hidden c-pt-6 mt-n6 c-lg:c-pt-5 c-lg:mt-n7 c-lg:mx-n6">
    <Swiper
      id="product-carousel"
      class="product-carousel row c-mx-0 w-100 c-mb-2 c-lg:c-mb-0 c-pb-0 c-flex-nowrap c-flex-grow-1 h-auto"
      :loop="images.length > 1"
      slides-per-view="auto"
      @slide-change="onSlideChange"
    >
      <SwiperSlide
        v-for="item in images"
        :key="item.id"
        class="col-12 c-lg:c-px-6 c-pb-5 cursor-pointer"
        @click="
          openModal({
            id: 'modal-fullscreen-product-image',
            title: productTitle !== '' ? productTitle : item.label,
            params: { images: images }
          })
        "
      >
        <div class="product-hero__image c-mb-0">
          <div
            class="product-hero__image__in embed-responsive"
            :class="{ 'no-padding': !item.has_padding }"
          >
            <RemoteResponsiveImage
              :product-image-data="item.data"
              :img-class="`embed-responsive-item relative ${
                item.has_padding ? 'image-contain' : 'image-cover'
              }`"
              :alt="item.alt || item.label"
              sizes="(min-width: 768px) 25vw, (min-width: 600px) 35vw, (min-width: 500px) 50vw, 70vw"
              :lazy-load="false"
            />
          </div>
        </div>
      </SwiperSlide>
    </Swiper>
    <ul
      v-if="images.length > 1"
      class="carousel-navigation"
    >
      <li
        v-for="(item, index) in images"
        :key="item.id"
      >
        <span
          :class="activeNavigationClass(index)"
          @click="toggleNavigation(index)"
        >{{ index }}</span>
      </li>
    </ul>
    <div
      v-if="images.length > 1"
      class="col-12 product-hero__arrows c-lg:c-pb-2 mr-n5"
    >
      <div class="flex c-items-center c-justify-start">
        <ButtonChevron
          direction="l"
          class="btn-warning text-primary c-p-4 btn-prev"
          data-slide="prev"
          aria-label="Previous slide"
          @click="prevSlide"
        />
        <ButtonChevron
          direction="r"
          class="btn-warning text-primary c-p-4 c-ml-4 btn-next"
          data-slide="next"
          aria-label="Next slide"
          @click="nextSlide"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { Swiper, SwiperSlide } from 'swiper/vue'
import { onMounted, ref } from 'vue'

import ButtonChevron from '@/components/buttons/ButtonChevron.vue'
import RemoteResponsiveImage from '@/components/general/BaseRemoteResponsiveImage.vue'
import { useModalStore } from '@/stores/ModalStore'

const { openModal } = useModalStore()

defineProps({
  images: {
    type: Array,
    default: () => [],
  },
  productTitle: {
    type: String,
    default: ''
  }
})

const activeNavigationIndex = ref(0)
const swiper = ref()
const imgWidth = ref(null)
const imgHeight = ref(null)

onMounted(() => {
  if (window.innerWidth <= 575) {
    imgHeight.value = 0
  }
  swiper.value = document.querySelector('#product-carousel').swiper
})

const activeNavigationClass = (index) => {
  return activeNavigationIndex.value === index ? 'active' : ''
}

const prevSlide = () => {
  if (swiper.value) swiper.value.slidePrev()
}

const nextSlide = () => {
  if (swiper.value) swiper.value.slideNext()
}

const toggleNavigation = (index) => {
  if (swiper.value) swiper.value.slideTo(index)
}

const onSlideChange = () => {
  if (swiper.value) activeNavigationIndex.value = swiper.value.realIndex
}
</script>

<style lang="scss" scoped>
.product-carousel {
  transition: 0.5s;
}

.carousel-navigation {
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  left: 0;
  right: 3.125rem;
  bottom: 3.125rem;
  margin: 0 0 0.625rem;
  padding: 1.25rem 0;

  li {
    &:not(:last-child) {
      margin-right: 12px;
    }

    span {
      width: 13px;
      height: 13px;
      border-radius: 50rem;
      border: none;
      transition: 0.3s width;

      &.active {
        background-color: #c55803;
        width: 36px;
      }

      &:before {
        width: 100%;
        height: 100%;
        background-color: rgba(197, 88, 3, 0.5);
        transform: none;
      }
    }
  }
}

@media screen and (min-width: 768px) {
  .carousel-navigation {
    right: 0;
    bottom: 0;
  }
}

@media screen and (min-width: 992px) {
  .carousel-navigation {
    right: 3.125rem;
    bottom: 3.125rem;
    margin-bottom: 0.625rem;
    padding: 1.25rem 0;
  }
}

@media screen and (max-width: 768px) {
  .carousel-navigation {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 0;
  }
}
</style>
